<template>
  <div v-if="availableAttachments.length" class="project-attachment-picker">
    <label v-if="label" :for="selectId">{{ label }}</label>
    <select
      :id="selectId"
      v-model="pickId"
      class="project-attachment-picker-select"
      @change="handlePick"
    >
      <option value="">{{ placeholder }}</option>
      <option
        v-for="attachment in unselectedAttachments"
        :key="attachment.id"
        :value="String(attachment.id)"
      >
        {{ attachment.filename }} ({{ attachmentSize(attachment) }})
      </option>
    </select>
    <div v-if="selectedIds.length" class="project-attachment-picker-selected">
      <div
        v-for="attachmentId in selectedIds"
        :key="attachmentId"
        class="project-attachment-picker-row"
      >
        <span class="project-attachment-picker-name">
          {{ attachmentById(attachmentId)?.filename }}
        </span>
        <span class="project-attachment-picker-size">
          {{ attachmentSize(attachmentById(attachmentId)) }}
        </span>
        <button
          type="button"
          class="btn-icon-small project-attachment-picker-delete"
          :title="deleteTitle"
          @click="removeAttachment(attachmentId)"
        >
          <TrashIcon />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import TrashIcon from './icons/TrashIcon.vue'

const props = defineProps({
  availableAttachments: {
    type: Array,
    default: () => [],
  },
  selectedIds: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    required: true,
  },
  deleteTitle: {
    type: String,
    required: true,
  },
  selectId: {
    type: String,
    default: 'project-attachment-picker-select',
  },
})

const emit = defineEmits(['update:selectedIds'])

const pickId = ref('')

const unselectedAttachments = computed(() => {
  const selected = new Set(props.selectedIds.map(id => String(id)))
  return [...props.availableAttachments]
    .filter(attachment => !selected.has(String(attachment.id)))
    .sort((a, b) => a.filename.localeCompare(b.filename, undefined, { sensitivity: 'base' }))
})

function handlePick() {
  const id = pickId.value
  if (!id) return

  if (!props.selectedIds.map(selectedId => String(selectedId)).includes(id)) {
    emit('update:selectedIds', [...props.selectedIds, id])
  }
  pickId.value = ''
}

function removeAttachment(attachmentId) {
  emit(
    'update:selectedIds',
    props.selectedIds.filter(id => String(id) !== String(attachmentId))
  )
}

function attachmentById(attachmentId) {
  return props.availableAttachments.find(
    attachment => String(attachment.id) === String(attachmentId)
  )
}

function attachmentSize(attachment) {
  return attachment?.humanSize || attachment?.human_size || ''
}
</script>

<style scoped>
.project-attachment-picker-select {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  margin-top: 4px;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #333;
  font-size: 14px;
}

.project-attachment-picker-selected {
  display: grid;
  gap: 8px;
  margin-top: 10px;
}

.project-attachment-picker-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}

.project-attachment-picker-name {
  min-width: 0;
  overflow: hidden;
  color: #333;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-attachment-picker-size {
  color: #666;
  font-size: 12px;
  white-space: nowrap;
}

.project-attachment-picker-delete {
  background: #fff;
  color: #666;
  border: 1px solid #ddd;
}

.project-attachment-picker-delete:hover {
  background: #f5f5f5;
  color: #333;
}
</style>
